import { useState, useRef, useEffect, type JSX } from 'react'
import { NavLink, useLocation } from 'react-router'
import {
  LayoutDashboard,
  BarChart3,
  Activity,
  Server,
  MessageSquare,
  Headphones,
  Headset,
  Phone,
  Voicemail,
  Settings,
  UserCircle,
  ChevronDown,
  ChevronRight,
  Users,
  UsersRound,
  Inbox,
  FileCheck,
  Contact,
  ScanSearch,
  Bell,
  Layers,
  MessageCircle,
  Building2,
  ArrowRightLeft,
  PhoneCall,
  PhoneIncoming,
  PhoneForwarded,
  ListTree,
  Megaphone,
  FlaskConical,
  ClipboardCheck,
  Gauge,
  Briefcase,
  CalendarDays,
  CalendarClock,
  Plane,
  Timer,
  Radio,
  CalendarRange,
  LineChart,
  UserCheck,
  Bot,
  BrainCircuit,
  Database,
  FileText,
  GraduationCap,
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore.js'

export interface NavItem {
  to?: string
  label: string
  icon: React.ReactNode
  requirePermission?: string
  children?: NavItem[]
}

export interface NavSection {
  label: string
  icon: React.ReactNode
  items: NavItem[]
  defaultExpanded?: boolean
  single?: boolean
}

export interface FlatSidebarItem {
  key: string
  label: string
  section: string
  parent?: string
}

export function flattenSidebarItems(): FlatSidebarItem[] {
  const result: FlatSidebarItem[] = []
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.children) {
        for (const child of item.children) {
          if (child.to) {
            result.push({ key: child.to, label: child.label, section: section.label, parent: item.label })
          }
        }
      } else if (item.to) {
        result.push({ key: item.to, label: item.label, section: section.label })
      }
    }
  }
  return result
}

export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Dashboard',
    icon: <BarChart3 className="h-[18px] w-[18px]" />,
    defaultExpanded: true,
    items: [
      { to: '/agent', label: 'My Dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
      // The agent's published shifts + live adherence (Workforce Management).
      { to: '/agent/workforce/my-schedule', label: 'My Schedule', icon: <CalendarDays className="h-4 w-4" /> },
      { to: '/agent/dashboard/pbx', label: 'PBX Dashboard', icon: <Server className="h-4 w-4" /> },
      { to: '/agent/dashboard/conversations', label: 'Conversation Dashboard', icon: <MessageSquare className="h-4 w-4" /> },
      { to: '/agent/dashboard/call-center', label: 'Call Center Dashboard', icon: <Headphones className="h-4 w-4" /> },
    ],
  },
  {
    label: 'Notifications',
    icon: <Bell className="h-[18px] w-[18px]" />,
    single: true,
    items: [
      { to: '/agent/notifications', label: 'Notifications', icon: <Bell className="h-4 w-4" /> },
    ],
  },
  {
    label: 'Conversation',
    icon: <MessageSquare className="h-[18px] w-[18px]" />,
    items: [
      // One inbox for every audience — "Mine" vs "All" and the SLA views are
      // tabs inside the page, not separate destinations.
      { to: '/agent/conversation/inbox', label: 'Inbox', icon: <Inbox className="h-4 w-4" /> },
      { to: '/agent/conversation/approvals', label: 'Approvals', icon: <FileCheck className="h-4 w-4" /> },
      // { to: '/agent/conversation/reassignment', label: 'Reassignment', icon: <Shuffle className="h-4 w-4" />, requirePermission: 'conversations:transfer' },
      { to: '/agent/conversation/omnichannel-queue', label: 'Omnichannel Queue', icon: <Layers className="h-4 w-4" /> },
      // { to: '/agent/conversation/search', label: 'Search Conversation', icon: <Search className="h-4 w-4" /> },
    ],
  },
  {
    label: 'Real Time Analytics',
    icon: <Activity className="h-[18px] w-[18px]" />,
    items: [
      // "Campaign Summary" points at the REAL-data page. The simulated
      // dashboard keeps its own route but is explicitly labelled as a demo:
      // the two are visually identical, so an unlabelled pair is how a
      // supervisor ends up reading invented figures as live operations.
      {
        to: '/agent/real-time-analytics/live-operations',
        label: 'Campaign Summary',
        icon: <BarChart3 className="h-4 w-4" />,
        requirePermission: 'supervisor:campaign_summary',
      },
      {
        to: '/agent/real-time-analytics/campaign-summary',
        label: 'Campaign Summary (Demo)',
        icon: <FlaskConical className="h-4 w-4" />,
        requirePermission: 'supervisor:campaign_summary',
      },
      {
        // Calls being transcribed by real-time AI assist, with live transcript.
        to: '/agent/real-time-analytics/live-call-assist',
        label: 'Live Call Assist',
        icon: <Radio className="h-4 w-4" />,
        requirePermission: 'supervisor:view',
      },
      {
        // Calls answered by an AI voice agent (phone + website): outcome,
        // AI summary, transcript, recording.
        to: '/agent/ai-voice/calls',
        label: 'AI Voice Calls',
        icon: <Bot className="h-4 w-4" />,
        requirePermission: 'calls:view',
      },
    ],
  },
  {
    // The same Knowledge / Prompts / Training managers as Admin → AI Center →
    // AI Agents, for agent-portal users holding the AI permissions (each item
    // is gated on the permission its API checks).
    label: 'AI Config',
    icon: <BrainCircuit className="h-[18px] w-[18px]" />,
    items: [
      { to: '/agent/ai-config/knowledge-base', label: 'Knowledge Base', icon: <Database className="h-4 w-4" />, requirePermission: 'ai:agents_view' },
      { to: '/agent/ai-config/prompts', label: 'Prompts', icon: <FileText className="h-4 w-4" />, requirePermission: 'admin:extensions:view' },
      { to: '/agent/ai-config/training', label: 'Training', icon: <GraduationCap className="h-4 w-4" />, requirePermission: 'admin:extensions:view' },
    ],
  },
  {
    // Supervisor-facing quality picture. The forms it scores against are
    // configured under Admin → Quality Management; this section is for
    // watching the results, not for administering them.
    label: 'Quality Management',
    icon: <ClipboardCheck className="h-[18px] w-[18px]" />,
    items: [
      {
        // The agent's own quality picture — scoped server-side to their
        // agent row. calls:view because an agent's evaluations live behind
        // the same permission as their call history.
        to: '/agent/quality-management/agent-dashboard',
        label: 'Agent Dashboard',
        icon: <Gauge className="h-4 w-4" />,
        requirePermission: 'calls:view',
      },
      {
        to: '/agent/quality-management/supervisor-dashboard',
        label: 'Supervisor Dashboard',
        icon: <LayoutDashboard className="h-4 w-4" />,
        requirePermission: 'supervisor:view',
      },
      {
        to: '/agent/quality-management/executive-dashboard',
        label: 'Executive Dashboard',
        icon: <Briefcase className="h-4 w-4" />,
        requirePermission: 'supervisor:view',
      },
    ],
  },
  {
    // Workforce Management — the agent's own schedule, adherence and
    // requests. Every agent sees these (the API scopes them to the caller's
    // agent row); the live adherence board is for supervisors.
    label: 'Workforce',
    icon: <CalendarClock className="h-[18px] w-[18px]" />,
    items: [
      { to: '/agent/workforce/dashboard', label: 'WFM Dashboard', icon: <LayoutDashboard className="h-4 w-4" />, requirePermission: 'supervisor:view' },
      { to: '/agent/workforce/time-off', label: 'Time Off', icon: <Plane className="h-4 w-4" /> },
      { to: '/agent/workforce/shift-swaps', label: 'Shift Swaps', icon: <ArrowRightLeft className="h-4 w-4" /> },
      {
        to: '/agent/workforce/real-time-adherence',
        label: 'Real-time Adherence',
        icon: <Timer className="h-4 w-4" />,
        requirePermission: 'supervisor:view',
      },
      // Supervisor planning — same pages as Admin → Workforce Management.
      { to: '/agent/workforce/schedule', label: 'Schedule', icon: <CalendarRange className="h-4 w-4" />, requirePermission: 'supervisor:view' },
      { to: '/agent/workforce/requests', label: 'Requests', icon: <Inbox className="h-4 w-4" />, requirePermission: 'supervisor:view' },
      { to: '/agent/workforce/adherence', label: 'Adherence', icon: <UserCheck className="h-4 w-4" />, requirePermission: 'supervisor:view' },
      { to: '/agent/workforce/forecasts', label: 'Forecasts', icon: <LineChart className="h-4 w-4" />, requirePermission: 'supervisor:view' },
    ],
  },
  {
    label: 'Company Directory',
    icon: <Users className="h-[18px] w-[18px]" />,
    single: true,
    items: [
      { to: '/agent/company-directory', label: 'Company Directory', icon: <Users className="h-4 w-4" /> },
    ],
  },
  {
    label: 'Clients',
    icon: <Contact className="h-[18px] w-[18px]" />,
    single: true,
    items: [
      { to: '/agent/clients', label: 'Clients', icon: <Contact className="h-4 w-4" />, requirePermission: 'clients:view' },
    ],
  },
  {
    label: 'Customer 360',
    icon: <ScanSearch className="h-[18px] w-[18px]" />,
    single: true,
    items: [
      { to: '/agent/customers', label: 'Customer 360', icon: <ScanSearch className="h-4 w-4" />, requirePermission: 'clients:view' },
    ],
  },
  {
    label: 'Chats',
    icon: <MessageCircle className="h-[18px] w-[18px]" />,
    single: true,
    items: [
      { to: '/agent/chat', label: 'Chat', icon: <MessageCircle className="h-4 w-4" />, requirePermission: 'conversations:view' },
    ],
  },
  {
    label: 'Communications',
    icon: <Phone className="h-[18px] w-[18px]" />,
    items: [
      // { to: '/agent/workspace', label: 'Workspace', icon: <LayoutDashboard className="h-[18px] w-[18px]" /> },
      // { to: '/agent/conversations', label: 'Conversations', icon: <MessageSquare className="h-[18px] w-[18px]" /> },
      // Recent Calls was removed from this menu — call history now lives in
      // Reports → Call Reports → Call History, which shows the same calls with
      // filters, analytics and export. The /agent/calls route still resolves,
      // so existing bookmarks and the new-tab quick link keep working.
      { to: '/agent/voicemail', label: 'Voicemail', icon: <Voicemail className="h-[18px] w-[18px]" />, requirePermission: 'voicemail:view_own' },
      // { to: '/agent/notifications', label: 'Notifications', icon: <Bell className="h-[18px] w-[18px]" /> },
      // Knowledge Base removed from Communications menu — still reachable by direct URL.
      // { to: '/agent/knowledge-base', label: 'Knowledge Base', icon: <BookOpen className="h-[18px] w-[18px]" />, requirePermission: 'admin:kb:view' },
    ],
  },
  {
    label: 'Reports',
    icon: <BarChart3 className="h-[18px] w-[18px]" />,
    items: [
      {
        // Grouped rather than flat: report categories are how people look for
        // a report ("the conversation ones"), and the list only grows.
        label: 'Conversation Reports',
        icon: <MessageSquare className="h-4 w-4" />,
        children: [
          {
            to: '/agent/reports/conversation/agent-performance',
            label: 'Agent Performance',
            icon: <Users className="h-4 w-4" />,
            requirePermission: 'conversations:view',
          },
          {
            to: '/agent/reports/conversation/detail',
            label: 'Conversation Detailed Report',
            icon: <Inbox className="h-4 w-4" />,
            requirePermission: 'conversations:view',
          },
          {
            to: '/agent/reports/conversation/location-performance',
            label: 'Location Performance',
            icon: <Building2 className="h-4 w-4" />,
            requirePermission: 'conversations:view',
          },
          {
            to: '/agent/reports/conversation/transfers',
            label: 'Conversation Transfer',
            icon: <ArrowRightLeft className="h-4 w-4" />,
            requirePermission: 'conversations:view',
          },
          {
            to: '/agent/reports/conversation/team-performance',
            label: 'Team Performance',
            icon: <UsersRound className="h-4 w-4" />,
            requirePermission: 'conversations:view',
          },
          {
            to: '/agent/reports/conversation/campaign-performance',
            label: 'Campaign Performance',
            icon: <Megaphone className="h-4 w-4" />,
            requirePermission: 'conversations:view',
          },
        ],
      },
      {
        // Voice reports, gated on `calls:view` rather than on the
        // conversation permission — a user allowed to read chat threads is not
        // thereby allowed to read the call detail record.
        label: 'Call Reports',
        icon: <Phone className="h-4 w-4" />,
        children: [
          {
            to: '/agent/reports/call/history',
            label: 'Call History',
            icon: <PhoneCall className="h-4 w-4" />,
            requirePermission: 'calls:view',
          },
          {
            to: '/agent/reports/call/ivr-selection',
            label: 'IVR Selection Report',
            icon: <ListTree className="h-4 w-4" />,
            requirePermission: 'calls:view',
          },
          {
            to: '/agent/reports/call/inbound',
            label: 'Inbound Call Report',
            icon: <PhoneIncoming className="h-4 w-4" />,
            requirePermission: 'calls:view',
          },
          {
            to: '/agent/reports/call/outbound',
            label: 'Outbound Call Report',
            icon: <PhoneForwarded className="h-4 w-4" />,
            requirePermission: 'calls:view',
          },
        ],
      },
    ],
  },
  {
    label: 'Contact Center Report',
    icon: <Headset className="h-[18px] w-[18px]" />,
    items: [
      {
        label: 'Agent Reports',
        icon: <Users className="h-4 w-4" />,
        children: [
          {
            to: '/agent/reports/call/agent-productivity-per-interval',
            label: 'Agent Productivity Per Interval',
            icon: <Activity className="h-4 w-4" />,
            requirePermission: 'calls:view',
          },
        ],
      },
    ],
  },
  {
    label: 'Settings',
    icon: <Settings className="h-[18px] w-[18px]" />,
    items: [
      // { to: '/agent/devices', label: 'Devices', icon: <Headphones className="h-4 w-4" /> },
      { to: '/agent/profile', label: 'Profile', icon: <UserCircle className="h-4 w-4" /> },
    ],
  },
]

interface AgentSidebarProps {
  collapsed: boolean
  onToggle: () => void
  showLinks?: boolean
}

export default function AgentSidebar({ collapsed, showLinks = true }: AgentSidebarProps): JSX.Element {
  const location = useLocation()
  const permissions = useAuthStore((s) => s.user?.permissions ?? [])
  const sidebarPermissions = useAuthStore((s) => s.user?.sidebarPermissions ?? null)

  const sidebarAllowed = (item: NavItem): boolean => {
    if (!sidebarPermissions || sidebarPermissions.length === 0) return true
    if (sidebarPermissions.includes('*')) return true
    if (item.to) return sidebarPermissions.includes(item.to)
    if (item.children) return item.children.some((c) => sidebarAllowed(c))
    return true
  }

  const allowed = (item: NavItem): boolean =>
    (!item.requirePermission
    || permissions.includes('*')
    || permissions.includes(item.requirePermission))
    && sidebarAllowed(item)

  /**
   * Drop items the user may not see, at EVERY depth.
   *
   * The filter used to run only over a section's top-level items, so a
   * `requirePermission` on a nested child was silently inert — which stopped
   * mattering while every child in a group shared one permission, and stops
   * being true the moment a section mixes them (Reports now holds both
   * `conversations:view` and `calls:view` groups). A group whose children all
   * filter out is dropped too, rather than left as a heading that expands to
   * nothing.
   */
  const visibleItems = (items: NavItem[]): NavItem[] => {
    const out: NavItem[] = []
    for (const item of items) {
      if (!allowed(item)) continue
      if (item.children) {
        const children = visibleItems(item.children)
        if (children.length === 0) continue
        out.push({ ...item, children })
      } else {
        out.push(item)
      }
    }
    return out
  }

  const isActive = (to: string): boolean => {
    if (to === '/agent') {
      return location.pathname === '/agent' || location.pathname === '/agent/'
    }
    // For routes that have distinct child routes (e.g. /agent/live-chat vs /agent/chats/facebook),
    // use exact match so the parent doesn't highlight when a child is active.
    const exactMatchRoutes = ['/agent/chat']
    if (exactMatchRoutes.includes(to)) {
      return location.pathname === to
    }
    return location.pathname === to || location.pathname.startsWith(`${to}/`)
  }

  const isItemActive = (item: NavItem): boolean => {
    if (item.to) return isActive(item.to)
    if (item.children) return item.children.some((child) => isItemActive(child))
    return false
  }

  const isSectionActive = (section: NavSection): boolean => {
    return section.items.some((item) => isItemActive(item))
  }

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(NAV_SECTIONS.filter((s) => s.defaultExpanded).map((s) => s.label))
  )

  // Auto-expand section containing the active route
  useEffect(() => {
    const activeSection = NAV_SECTIONS.find((section) => isSectionActive(section))
    if (activeSection) {
      setExpandedSections(new Set([activeSection.label]))
    }
  }, [location.pathname])

  const toggleSection = (label: string) => {
    setExpandedSections((prev) => {
      // Accordion: close all others, toggle clicked one
      if (prev.has(label)) {
        return new Set() // close if already open
      }
      return new Set([label]) // open only this one
    })
  }

  // Collapsed flyout popup state — carries the hovered section's viewport `top`
  // because the flyout is position:fixed (an absolute flyout would be clipped
  // by the nav's overflow-y scroll container).
  const [popupSection, setPopupSection] = useState<{ label: string; top: number } | null>(null)
  const popupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showPopup = (label: string, top: number) => {
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current)
    // Keep the flyout inside the viewport when the section sits near the bottom.
    setPopupSection({ label, top: Math.min(top, window.innerHeight - 320) })
  }

  const hidePopup = () => {
    popupTimeoutRef.current = setTimeout(() => setPopupSection(null), 150)
  }

  // Sub-group expand state for nested items — start closed, auto-open active
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  // Auto-expand nested group containing the active route
  useEffect(() => {
    const findActiveGroup = (items: NavItem[]): string | null => {
      for (const item of items) {
        if (item.children) {
          if (item.children.some((child) => isItemActive(child))) return item.label
          const nested = findActiveGroup(item.children)
          if (nested) return nested
        }
      }
      return null
    }
    for (const section of NAV_SECTIONS) {
      const activeGroup = findActiveGroup(section.items)
      if (activeGroup) {
        setExpandedGroups((prev) => {
          const next = new Set(prev)
          next.add(activeGroup)
          return next
        })
        break
      }
    }
  }, [location.pathname])

  const toggleGroup = (label: string, siblingLabels: string[]) => {
    setExpandedGroups((prev) => {
      // Accordion within section: close all siblings, toggle clicked one
      const next = new Set<string>()
      // Keep groups from OTHER sections that are open
      prev.forEach((g) => {
        if (!siblingLabels.includes(g)) next.add(g)
      })
      if (!prev.has(label)) {
        next.add(label)
      }
      return next
    })
  }

  const renderNavItem = (item: NavItem, depth: number, siblingLabels?: string[]): JSX.Element | null => {
    const hasChildren = !!item.children && item.children.length > 0
    const itemActive = isItemActive(item)

    if (hasChildren) {
      const isGroupExpanded = expandedGroups.has(item.label)
      // Collect sibling group labels at this nesting level
      const childSiblingLabels = item.children?.filter((c) => c.children).map((c) => c.label) ?? []
      return (
        <div key={item.label} className="flex flex-col">
          <button
            onClick={() => toggleGroup(item.label, siblingLabels ?? [])}
            className={`flex items-center gap-2.5 rounded-[3px] transition-all px-3 py-2 text-[13px] font-medium ${
              itemActive ? 'text-[#3366FF] bg-blue-50/70' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
            }`}
            style={{ paddingLeft: `${12 + depth * 14}px` }}
          >
            <span className={`flex-shrink-0 transition-colors ${itemActive ? 'text-[#3366FF]' : 'text-gray-400'}`}>
              {item.icon}
            </span>
            <span className={`flex-1 text-left text-[13px] font-medium truncate ${itemActive ? 'text-[#3366FF]' : 'text-gray-600'}`}>
              {item.label}
            </span>
            <span className={`transition-transform duration-200 ${itemActive ? 'text-[#3366FF]' : 'text-gray-400'}`}>
              {isGroupExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
          </button>
          {isGroupExpanded && (
            <div className="mt-1 flex flex-col gap-1">
              {item.children!.map((child) => renderNavItem(child, depth + 1, childSiblingLabels))}
            </div>
          )}
        </div>
      )
    }

    if (!item.to) return null

    const active = isActive(item.to)
    return (
      <NavLink
        key={item.to}
        to={item.to}
        className={`group flex items-center gap-2.5 rounded-[3px] transition-all ${
          active
            ? 'bg-[#3366FF] text-white shadow-sm shadow-blue-200/60'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        } px-3 py-2`}
        style={{ marginLeft: `${depth * 10}px` }}
      >
        <span className={`flex-shrink-0 transition-colors ${active ? 'text-white' : 'text-gray-400 group-hover:text-gray-500'}`}>
          {item.icon}
        </span>
        <span className={`text-[13px] font-medium truncate ${active ? 'text-white' : 'text-gray-600'}`}>
          {item.label}
        </span>
      </NavLink>
    )
  }

  const renderFlyoutItem = (item: NavItem, depth: number): JSX.Element | null => {
    const hasChildren = !!item.children && item.children.length > 0

    if (hasChildren) {
      return (
        <div key={item.label} className="flex flex-col">
          <div className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-1 whitespace-nowrap ${depth > 0 ? 'pl-4' : ''}`}>
            {item.label}
          </div>
          {item.children!.map((child) => renderFlyoutItem(child, depth + 1))}
        </div>
      )
    }

    if (!item.to) return null

    const active = isActive(item.to)
    return (
      <NavLink
        key={item.to}
        to={item.to}
        className={`flex items-center gap-2.5 mx-1.5 px-2.5 py-2 rounded-[3px] text-xs font-medium whitespace-nowrap transition-all ${
          active
            ? 'bg-[#3366FF] text-white shadow-sm shadow-blue-200/60'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
        style={{ marginLeft: `${4 + depth * 8}px` }}
      >
        <span className={`flex-shrink-0 ${active ? 'text-white' : 'text-gray-400'}`}>
          {item.icon}
        </span>
        <span className={active ? 'text-white' : ''}>{item.label}</span>
      </NavLink>
    )
  }

  return (
    <aside
      className={`flex flex-col border-r border-gray-200/70 bg-white transition-all duration-300 ease-in-out ${
        collapsed ? 'w-[72px]' : 'w-72'
      }`}
    >
      {/* Navigation */}
      <nav className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-3 py-4 nav-scroll">
        {!showLinks && <></>}
        {showLinks && NAV_SECTIONS.map((section) => {
          const sectionActive = isSectionActive(section)
          const isExpanded = expandedSections.has(section.label)
          const filteredItems = visibleItems(section.items)
          if (filteredItems.length === 0) return null

          if (collapsed) {
            // Single-item section: icon IS the link
            if (section.single && filteredItems.length === 1) {
              const item = filteredItems[0]!
              if (!item.to) return null
              const active = isActive(item.to)
              return (
                <NavLink
                  key={section.label}
                  to={item.to}
                  className={`group relative flex items-center justify-center rounded-[3px] transition-all py-3.5 ${
                    active
                      ? 'bg-[#3366FF] text-white shadow-sm shadow-blue-200/60'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  title={item.label}
                >
                  <span className={`transition-colors ${active ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                    {section.icon}
                  </span>
                  {active && (
                    <div className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-[#3366FF]" />
                  )}
                </NavLink>
              )
            }

            // Multi-item section: icon button + hover flyout
            return (
              <div
                key={section.label}
                className="relative flex flex-col"
                onMouseEnter={(e) => showPopup(section.label, e.currentTarget.getBoundingClientRect().top)}
                onMouseLeave={hidePopup}
              >
                <button
                  className={`flex items-center justify-center rounded-[3px] transition-all py-3.5 ${
                    sectionActive
                      ? 'bg-[#3366FF] text-white shadow-sm shadow-blue-200/60'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  title={section.label}
                >
                  <span className={`transition-colors ${sectionActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                    {section.icon}
                  </span>
                  {sectionActive && (
                    <div className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-[#3366FF]" />
                  )}
                </button>

                {popupSection?.label === section.label && (
                  <div
                    className="fixed z-50 w-max max-h-[80vh] overflow-y-auto rounded-[3px] border border-gray-200/80 bg-white py-2 shadow-xl shadow-gray-300/40 nav-scroll"
                    style={{ left: 76, top: popupSection.top }}
                    onMouseEnter={() => {
                      if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current)
                    }}
                    onMouseLeave={hidePopup}
                  >
                    <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100 mb-1 whitespace-nowrap">
                      {section.label}
                    </div>
                    {filteredItems.map((item) => renderFlyoutItem(item, 0))}
                  </div>
                )}
              </div>
            )
          }

          // Single flat menu item (no expand/collapse)
          if (section.single && filteredItems.length === 1) {
            const item = filteredItems[0]!
            if (!item.to) return null
            const active = isActive(item.to)
            return (
              <div key={section.label} className="flex flex-col">
                <NavLink
                  to={item.to}
                  className={`group relative flex items-center gap-3 rounded-[3px] px-3 py-2.5 transition-all ${
                    active
                      ? 'bg-[#3366FF] text-white shadow-sm shadow-blue-200/60'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  {active && (
                    <div className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-white/80" />
                  )}
                  <span className={`transition-colors ${active ? 'text-white' : 'text-gray-500 group-hover:text-gray-700'}`}>
                    {section.icon}
                  </span>
                  <span className={`flex-1 text-left text-sm font-semibold ${active ? 'text-white' : 'text-gray-800'}`}>
                    {section.label}
                  </span>
                </NavLink>
              </div>
            )
          }

          return (
            <div key={section.label} className="flex flex-col">
              {/* Section header */}
              <button
                onClick={() => toggleSection(section.label)}
                className={`group flex items-center gap-3 rounded-[3px] px-3 py-2.5 transition-all ${
                  sectionActive
                    ? 'bg-blue-50/70 text-[#3366FF]'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className={`transition-colors ${sectionActive ? 'text-[#3366FF]' : 'text-gray-500 group-hover:text-gray-700'}`}>
                  {section.icon}
                </span>
                <span className={`flex-1 text-left text-sm font-semibold ${sectionActive ? 'text-[#3366FF]' : 'text-gray-800'}`}>
                  {section.label}
                </span>
                <span className={`transition-transform duration-200 ${sectionActive ? 'text-[#3366FF]' : 'text-gray-400'}`}>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </span>
              </button>

              {/* Section items */}
              {isExpanded && (
                <div className="mt-1 flex flex-col gap-1 pl-3 pr-1">
                  {(() => {
                    const siblingLabels = filteredItems.filter((i) => i.children).map((i) => i.label)
                    return filteredItems.map((item) => renderNavItem(item, 0, siblingLabels))
                  })()}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Bottom status */}
      <div className="mt-auto px-3 pb-4 pt-3">
        <div
          className={`rounded-[3px] border border-emerald-100 bg-emerald-50/60 p-3 ${
            collapsed ? 'flex justify-center' : ''
          }`}
        >
          {collapsed ? (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
          ) : (
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[11px] font-medium text-emerald-700">System Online</span>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}
